/**
 * Loads a Google Map + Street View panorama for a store on demand.
 * Nothing is fetched from Google until the visitor clicks the "load map" button
 * (a self-contained click-to-load gate, independent of any consent-management setup).
 */

/**
 * There's no @types/google.maps package in this project, so `window.google` isn't a
 * known property of Window — cast through `any` once here instead of at every call site.
 * @returns {any}
 */
function getGoogleMaps() {
  return /** @type {any} */ (window).google.maps;
}

/**
 * Returns an OverlayView subclass (bound to the given `maps` namespace) that renders a
 * small floating text label above a marker position. Used instead of InfoWindow, whose
 * built-in close-button chrome doesn't fit a permanent single-line store name label.
 * @param {any} maps
 */
function createLabelOverlayClass(maps) {
  return class StoreLabelOverlay extends maps.OverlayView {
    /**
     * @param {any} map
     * @param {any} position
     * @param {string} text
     */
    constructor(map, position, text) {
      super();
      this.position = position;
      this.text = text;
      /** @type {HTMLElement | null} */
      this.div = null;
      this.setMap(map);
    }

    onAdd() {
      const div = document.createElement('div');
      div.className = 'ew-store-details__map-label';
      div.textContent = this.text;
      this.div = div;
      this.getPanes().floatPane.appendChild(div);
    }

    draw() {
      if (!this.div) return;
      const point = this.getProjection().fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
    }

    onRemove() {
      this.div?.remove();
      this.div = null;
    }
  };
}

class EwStoreDetailsMap extends HTMLElement {
  /** @type {Promise<void> | null} */
  static _apiPromise = null;

  _loaded = false;

  /** @type {HTMLElement | null} */
  mapGrid = null;
  /** @type {HTMLElement | null} */
  mapCanvas = null;
  /** @type {HTMLElement | null} */
  streetViewCanvas = null;
  /** @type {HTMLElement | null} */
  placeholder = null;
  /** @type {HTMLElement | null} */
  loadButton = null;

  connectedCallback() {
    this.placeId = this.dataset.placeId || '';
    this.panoId = this.dataset.panoId || '';
    this.apiKey = this.dataset.apiKey || '';
    this.storeName = this.dataset.storeName || '';
    this.storeAddress = this.dataset.storeAddress || '';
    this.mapsUrl = this.dataset.mapsUrl || '';
    this.mapLabel = this.dataset.mapLabel || '';
    this.openInMapsLabel = this.dataset.openInMapsLabel || '';

    this.mapGrid = /** @type {HTMLElement | null} */ (this.querySelector('[data-ew-map-grid]'));
    this.mapCanvas = /** @type {HTMLElement | null} */ (this.querySelector('[data-ew-map]'));
    this.streetViewCanvas = /** @type {HTMLElement | null} */ (this.querySelector('[data-ew-streetview]'));
    this.placeholder = /** @type {HTMLElement | null} */ (this.querySelector('[data-ew-map-placeholder]'));
    this.loadButton = /** @type {HTMLElement | null} */ (this.querySelector('[data-ew-load-map]'));

    if (!this.placeId || !this.apiKey || !this.loadButton) return;

    this.loadButton.addEventListener('click', this._handleLoadClick);
  }

  disconnectedCallback() {
    this.loadButton?.removeEventListener('click', this._handleLoadClick);
  }

  _handleLoadClick = () => {
    if (this.placeholder) this.placeholder.hidden = true;
    if (this.mapGrid) this.mapGrid.hidden = false;

    this._load();
  };

  async _load() {
    if (this._loaded) return;
    this._loaded = true;

    try {
      await EwStoreDetailsMap._loadGoogleMaps(this.apiKey);

      // Loaded via the classic <script src="maps/api/js"> bundle (not the newer
      // importLibrary() loader), so Map/Marker/Geocoder/StreetView are already
      // available directly on window.google.maps once the script has loaded.
      const maps = getGoogleMaps();
      const { results } = await new maps.Geocoder().geocode({ placeId: this.placeId });
      const location = results && results[0] && results[0].geometry && results[0].geometry.location;

      if (!location) return;

      if (this.mapCanvas) {
        const map = new maps.Map(this.mapCanvas, {
          center: location,
          zoom: 16,
          fullscreenControl: false,
          streetViewControl: false,
        });

        const marker = new maps.Marker({ map, position: location });

        if (this.mapLabel) {
          new (createLabelOverlayClass(maps))(map, location, this.mapLabel);
        }

        if (this.storeName) {
          const content = document.createElement('div');

          if (this.storeAddress) {
            const address = document.createElement('div');
            address.textContent = this.storeAddress;
            content.appendChild(address);
          }

          if (this.mapsUrl && this.openInMapsLabel) {
            const link = document.createElement('a');
            link.href = this.mapsUrl;
            link.target = '_blank';
            link.rel = 'noopener';
            link.textContent = this.openInMapsLabel;
            content.appendChild(link);
          }

          const infoWindow = new maps.InfoWindow({
            headerContent: this.storeName,
            content,
            ariaLabel: this.storeName,
          });

          marker.addListener('click', () => infoWindow.open({ anchor: marker, map }));
        }
      }

      if (this.streetViewCanvas) {
        await this._renderStreetView(this.streetViewCanvas, location);
      }
    } catch (error) {
      console.warn('EwStoreDetailsMap: could not load Google Maps.', error);
    }
  }

  /**
   * @param {HTMLElement} streetViewCanvas
   * @param {any} location
   */
  async _renderStreetView(streetViewCanvas, location) {
    const maps = getGoogleMaps();

    if (this.panoId) {
      new maps.StreetViewPanorama(streetViewCanvas, { pano: this.panoId });
      streetViewCanvas.hidden = false;
      return;
    }

    try {
      const { data } = await new maps.StreetViewService().getPanorama({ location, radius: 50 });
      const pano = data && data.location && data.location.pano;

      if (!pano) return;

      new maps.StreetViewPanorama(streetViewCanvas, { pano });
      streetViewCanvas.hidden = false;
    } catch {
      // No panorama found near this location — leave the Street View panel hidden.
    }
  }

  /**
   * @param {string} apiKey
   * @returns {Promise<void>}
   */
  static _loadGoogleMaps(apiKey) {
    const w = /** @type {any} */ (window);

    if (w.google && w.google.maps && w.google.maps.Geocoder) return Promise.resolve();
    if (EwStoreDetailsMap._apiPromise) return EwStoreDetailsMap._apiPromise;

    EwStoreDetailsMap._apiPromise = new Promise((resolve, reject) => {
      // No `loading=async` here — that switches Google's loader into the lazy,
      // importLibrary()-only mode where classes like Geocoder aren't eagerly attached.
      // The `callback` param is the documented way to know the full API (not just the
      // script file) is ready; script.onload alone fires too early for that.
      w.__ewStoreDetailsMapsReady = () => {
        delete w.__ewStoreDetailsMapsReady;
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&callback=__ewStoreDetailsMapsReady`;
      script.async = true;
      script.onerror = () => reject(new Error('Google Maps script failed to load.'));
      document.head.appendChild(script);
    });

    return EwStoreDetailsMap._apiPromise;
  }
}

if (!customElements.get('ew-store-details-map')) {
  customElements.define('ew-store-details-map', EwStoreDetailsMap);
}
